import { useState } from 'react'
import SVG from 'react-inlinesvg'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useContextUI } from '../../contexts/UIContext'
import { UIElement } from '../../types/UIElements'
import { useEnv } from '../../utils/useEnv'
import { TotalMotifCount } from './TotalMotifCount'

const CornerDecoration = styled.div<{
  corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
}>`
  position: absolute;
  width: 112px;
  height: 112px;
  background-color: ${({ theme }) => theme.colors.black};

  --decoration-offset: -20px;

  border-${({ corner }) => corner}-radius: 20px;

  ${({ corner }) => {
    switch (corner) {
      case 'top-left':
        return `
        top: var(--decoration-offset); 
        left: var(--decoration-offset);
      `
      case 'top-right':
        return `
        top: var(--decoration-offset); 
        right: var(--decoration-offset);
      `
      case 'bottom-left':
        return `
        bottom: var(--decoration-offset); 
        left: var(--decoration-offset);
      `
      case 'bottom-right':
        return `
        bottom: var(--decoration-offset); 
        right: var(--decoration-offset);
      `
      default:
        return `
        top: var(--decoration-offset); 
        left: var(--decoration-offset);
      `
    }
  }}
`

const WelcomeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;

  opacity: 0;
  transition: opacity 0.3s;

  &.fade-in {
    opacity: 1;
  }

  &.kiosk {
    user-select: none;
  }

  .container {
    width: 580px;
    height: 484px;

    position: relative;

    transform: scale(0.42);

    @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.sm}) {
      transform: scale(0.8);
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.lg}) {
      transform: scale(1);
    }

    .content-container {
      width: 100%;
      height: 100%;

      display: grid;
      place-items: center;

      position: absolute;

      border-radius: 12px;

      background-color: ${({ theme }) => theme.colors.white};

      .wrapper {
        display: flex;
        gap: 16px;
        flex-direction: column;
        align-items: center;
      }

      h2,
      h3 {
        text-align: center;
        margin: 0;
      }

      .appLogo {
        width: 100%;
        height: auto;
        margin-bottom: 16px;
      }

      h2 {
        font-size: 2.05rem;
        font-family: ${({ theme }) => theme.font.medium};
        font-weight: 500;
      }

      .attribution-wrapper {
        width: 100%;
        display: flex;
        justify-content: center;
        position: relative;
      }

      h3 {
        white-space: pre-wrap;
        font-size: 1.25rem;
        font-family: ${({ theme }) => theme.font.regular};
        width: fit-content;
        background-color: ${({ theme }) => theme.colors.white};
        font-weight: 400;
        padding: 0 12px;
      }
    }
  }

  .bottom-wrapper {
    width: 320px;

    .call-to-action {
      width: 100%;
      text-align: center;
      padding: 4px 0;
      font-size: 1.5rem;
      font-family: ${({ theme }) => theme.font.medium};
      font-weight: 500;
      color: ${({ theme }) => theme.colors.white};
      background-color: ${({ theme }) => theme.colors.black};
      border-radius: 12px;
      margin-bottom: 12px;
    }

    .footer {
      display: flex;
      justify-content: space-between;
      align-items: center;

      svg {
        height: 32px;
        width: auto;
      }
    }
  }

  &.fade-out {
    animation: fade-out-y-10p 0.8s ease forwards;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.sm}) {
    .bubble-wrapper {
      transform: scale(0.8);
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.lg}) {
    .bubble-wrapper {
      transform: scale(1);
    }
  }
`

const Welcome = () => {
  const { t, i18n } = useTranslation()

  const [fadeOut, setFadeOut] = useState<boolean>(false)
  const { elementsOpen, setElementsOpen } = useContextUI()
  const { isKiosk } = useEnv()

  const onClick = () => {
    setFadeOut(true)
    setTimeout(() => {
      setFadeOut(false)
      setElementsOpen([UIElement.CONTROLS])
    }, 500)
  }

  return elementsOpen.includes(UIElement.WELCOME) ? (
    <WelcomeWrapper
      onClick={onClick}
      className={`${fadeOut ? 'fade-out' : ''} ${isKiosk ? 'kiosk' : ''}
        fade-in
      `}
    >
      <div className="container">
        <CornerDecoration corner="top-left" />
        <CornerDecoration corner="top-right" />
        <CornerDecoration corner="bottom-left" />
        <CornerDecoration corner="bottom-right" />

        <div className="content-container">
          <div className="wrapper">
            <SVG className="appLogo" src={`images/app_title.svg`} />
            <SVG className="appLogo" src={`images/app_subtitle.svg`} />
            <div className="bottom-wrapper">
              <div className="call-to-action">{t('chooseAndCombine')}</div>
              <div className="footer">
                <SVG src="/icons/logo.svg" />

                <TotalMotifCount />
              </div>
            </div>
          </div>
        </div>
      </div>
    </WelcomeWrapper>
  ) : null
}

export default Welcome
